// Sprite selector component – bug‑fix: selector was moving even when sizeChanged & centreShifted were false.
//
//   • Introduces a MIN_SIZE_DELTA_PX dead‑band (1 px): tiny sprite jitter no longer counts.
//   • Removes the previous `needExpand` shortcut; expansion is folded into sizeChanged logic.
//   • diffBounds now flags sizeChanged if either the *relative* or *absolute* delta crosses
//     thresholds (percentage **or** ≥ MIN_SIZE_DELTA_PX).
//   • Keeps a 10 % outer margin around the sprite while remaining perfectly still otherwise.

const html = require("choo/html");
const SelectSprite = require("../elements/selectsprite.js");
const ButtonIcon = require("../elements/buttonicon.js");
const {
  getNormalizedTargetBounds,
} = require("../../shared_utils/coordinates.js");

const GAMESCREEN_ASPECT_RATIO = 1.333;
const MIN_WIDTH = 5;
const MIN_HEIGHT = MIN_WIDTH * GAMESCREEN_ASPECT_RATIO;

const {
  BUILTIN_PHYSICS,
} = require("../../shared_utils/gamebender-constants.js");
const AppliedGlitches = require("../elements/applied-glitches.js");

// ────────────────────────────────────────────────────────────────────────────────
// Tunables
// ────────────────────────────────────────────────────────────────────────────────
const SIZE_PCT_THRESHOLD = 0.15;
const CENTER_PX_THRESHOLD = 1;
const OUTER_MARGIN_RATIO = 0.15;
const MIN_SIZE_DELTA_PX = 1; //

module.exports = function SpriteSelector(state, emit) {
  if (!state.selectedTargetId) return null;

  // ──────────────────────────────────────────────────────
  // Input handlers (null‑ed in restricted states)
  // ──────────────────────────────────────────────────────
  let onRotate = (e) => {
    e.preventDefault();
    emit("start-rotating-target");
  };
  let onResize = (e, r) => {
    e.preventDefault();
    emit("start-resizing-target", e, r);
  };
  let onMove = (e) => {
    e.preventDefault();
    emit("start-moving-target", e);
  };

  if (
    state.isFullscreen ||
    (state.playingTutorial && !state.currentTutorial.spriteTransformsAllowed)
  ) {
    onMove = onRotate = onResize = null;
  }

  // ──────────────────────────────────────────────────────
  // Measure sprite bounds (normalised → always axis‑aligned)
  // ──────────────────────────────────────────────────────
  const target = state.vm.runtime.targets.find(
    (t) => t.id === state.selectedTargetId
  );
  if (!target) return null;

  const originalDir = target.direction;
  target.setDirection(90);
  const sprite = getNormalizedTargetBounds(target, state.canvas);
  target.setDirection(originalDir);
  if (!sprite) return null;

  // Apply 10 % margin (5 % each side)
  const marginX = sprite.width * OUTER_MARGIN_RATIO * 0.5;
  const marginY = sprite.height * OUTER_MARGIN_RATIO * 0.5;
  const padded = {
    top: sprite.top - marginY,
    left: sprite.left - marginX,
    width: sprite.width + marginX * 2,
    height: sprite.height + marginY * 2,
    targetX: target.x,
    targetY: target.y,
  };

  // ──────────────────────────────────────────────────────
  // De‑jitter logic – independent size / centre thresholds
  // ──────────────────────────────────────────────────────
  let last = state.lastSelectorBounds;
  if (!last) {
    last = {
      width: 0.000001,
      height: 0.0000001,
      top: -10000,
      left: -10000,
      targetX: -10000,
      targetY: -10000,
    };
  }
  const { sizeChanged, centreShifted } = diffBounds(last, padded);
  let width, height, top, left;

  if (!state.resizingSelectedTarget && last) {
    // Start with previous stable values
    ({ width, height, top, left } = last);

    // Update size (and re‑centre) only if significant
    if (sizeChanged) {
      width = Math.max(padded.width, MIN_WIDTH);
      height = Math.max(padded.height, MIN_HEIGHT);

      const cx = padded.left + padded.width / 2;
      const cy = padded.top + padded.height / 2;
      top = cy - height / 2;
      left = cx - width / 2;
    }

    // Update centre only (keep W/H) if significant
    if (!sizeChanged && centreShifted) {
      const cx = padded.left + padded.width / 2;
      const cy = padded.top + padded.height / 2;
      top = cy - height / 2;
      left = cx - width / 2;
    }
  } else {
    // Live resize or first render
    width = Math.max(padded.width, MIN_WIDTH);
    height = Math.max(padded.height, MIN_HEIGHT);

    const cx = padded.left + padded.width / 2;
    const cy = padded.top + padded.height / 2;
    top = cy - height / 2;
    left = cx - width / 2;
  }
  let targetX = last.targetX;
  let targetY = last.targetY;
  if (centreShifted) {
    targetX = padded.targetX;
    targetY = padded.targetY;
  }

  // Remember for next frame
  state.lastSelectorBounds = {
    width,
    height,
    top,
    left,
    targetX,
    targetY,
  };

  // ──────────────────────────────────────────────────────
  // Render selector element
  // ──────────────────────────────────────────────────────
  const disabled =
    state.selectedVariableId !== null ||
    (state.playingTutorial && !state.currentTutorial.spriteTransformsAllowed);

  const hover = SelectSprite({
    width,
    height,
    top,
    left,
    rotation: target.direction - 90,
    resizing: state.resizingSelectedTarget,
    moving: state.movingSelectedTarget,
    disabled,
    hideRotationHandle: state.isFullscreen,
    onRotate,
    onResize,
    onMove,
  });

  // ──────────────────────────────────────────────────────
  // Glitch badge overlay (optional)
  // ──────────────────────────────────────────────────────
  const originalId = target.sprite.clones[0].id;
  const glitchApps = state.glitchApplications
    .filter((g) => g.targetId === originalId && g.glitchId !== BUILTIN_PHYSICS)
    .map((h) => ({
      ...state.availableGlitches.find((g) => g.id === h.glitchId),
      applicationId: h.id,
    }));

  const appliedGlitches = state.showGlitches
    ? AppliedGlitches({
        top: top + height,
        left,
        glitches: glitchApps,
        width,
        height,
        beginDrag: (e, glitch) => {
          const dragSticker = ButtonIcon({
            size: "small",
            color: glitch.color,
            icon: glitch.icon,
          });
          emit("start-drag", dragSticker, e);
          emit("select-glitch-application", glitch.applicationId);
          emit("start-dragging-glitch-application");
        },
        selectedGlitchApplicationId: state.selectedGlitchApplicationId,
      })
    : null;

  return [hover, state.isFullscreen ? null : appliedGlitches];
};

// ────────────────────────────────────────────────────────────────────────────────
// Helper – returns booleans for independent size / centre thresholds
// ────────────────────────────────────────────────────────────────────────────────
function diffBounds(a, b) {
  if (!a || !b) return { sizeChanged: true, centreShifted: true };

  const widthDelta = Math.abs(a.width - b.width);
  const heightDelta = Math.abs(a.height - b.height);
  const sizeChanged =
    (widthDelta / b.width > SIZE_PCT_THRESHOLD ||
      heightDelta / b.height > SIZE_PCT_THRESHOLD) &&
    (widthDelta > MIN_SIZE_DELTA_PX || heightDelta > MIN_SIZE_DELTA_PX);

  // Centre delta
  const targetXDelta = Math.abs(a.targetX - b.targetX);
  const targetYDelta = Math.abs(a.targetY - b.targetY);

  const centreShifted =
    targetXDelta > CENTER_PX_THRESHOLD || targetYDelta > CENTER_PX_THRESHOLD;

  return { sizeChanged, centreShifted };
}
